import type { AvailableShift, ClinicalBridgePreBidDate } from '../index.js'

import type { HolidayEvent } from '../../facilities/index.js'

import type { ShiftOutcome } from '../../partner/index.js'

import type { DayMark } from './scheduleDayMarks.js'

/** Everything the schedule calendar knows about one date (`YYYY-MM-DD`). */
export interface ScheduleDay {
  date: string
  day: number
  /** The worker's own reported shifts on this date. */
  outcomes: readonly ShiftOutcome[]
  /** The facility's open shifts on this date. */
  available: readonly AvailableShift[]
  /** The worker's Clinical Bridge pre-bid for this date, if any. */
  bridge: ClinicalBridgePreBidDate | null
  /** A holiday event whose period covers this date, if any. */
  holiday: HolidayEvent | null
  /** The worker's shift on the released schedule this date, as it stands after swaps and pickups; null when none. */
  scheduled: ScheduledShift | null
  /** The worker's shift on their draft of a month not yet released this date; null when none. A draft claims nothing. */
  drafted: ScheduledShift | null
  /** How the day came to the worker since the release, what was taken off, offered, lost or asked off; null when nothing. */
  mark: DayMark | null
  isToday: boolean
}

/** A shift on the worker's released schedule, as the result or the board answers it. */
export interface ScheduledShift {
  date: string
  shiftType: string
  startTime: string
  endTime: string
  unitId: string
}

export interface ScheduleMonth {
  /** `YYYY-MM`. */
  month: string
  label: string
  /** Empty cells before the 1st, so it falls under its weekday. */
  leadingBlanks: number
  days: readonly ScheduleDay[]
}

export interface ScheduleRecords {
  outcomes: readonly ShiftOutcome[]
  available: readonly AvailableShift[]
  bridgeDates: readonly ClinicalBridgePreBidDate[]
  holidays: readonly HolidayEvent[]
  /** The worker's shifts on the released schedule of the months shown; absent reads as none. */
  scheduled?: readonly ScheduledShift[]
  /** The worker's drafted shifts for a month not yet released; absent reads as none. */
  drafted?: readonly ScheduledShift[]
  /** Each released month's marks (`dayMarksOf`); absent reads as none. */
  marks?: readonly DayMark[]
  /** The facility's units by id, named as it calls them; absent or missing an id reads as the id. */
  unitNames?: ReadonlyMap<string, string>
}

const pad = (value: number): string => String(value).padStart(2, '0')

/** Today's date as the browser's calendar reads it. */
export function todayDate(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** The month `offset` months from the one `month` (`YYYY-MM`) names. */
export function shiftMonth(month: string, offset: number): string {
  const [year, monthNumber] = month.split('-').map(Number)
  const moved = new Date(Date.UTC(year ?? 1970, (monthNumber ?? 1) - 1 + offset, 1))
  return `${moved.getUTCFullYear()}-${pad(moved.getUTCMonth() + 1)}`
}

function hasAnything(day: Pick<ScheduleDay, 'outcomes' | 'available' | 'bridge' | 'holiday' | 'scheduled' | 'drafted' | 'mark'>): boolean {
  return day.outcomes.length > 0 || day.available.length > 0 || day.bridge !== null || day.holiday !== null || day.scheduled !== null || day.drafted !== null || day.mark !== null
}

/** A day opens its details when something is on it, or when the worker could enroll for it. */
export function dayHasDetails(day: ScheduleDay, enrollable: (date: string) => boolean = () => false): boolean {
  return hasAnything(day) || enrollable(day.date)
}

/** One calendar month with everything filed under each of its dates. */
export function buildScheduleMonth(month: string, records: ScheduleRecords, today: string): ScheduleMonth {
  const [year, monthNumber] = month.split('-').map(Number)
  const first = new Date(Date.UTC(year ?? 1970, (monthNumber ?? 1) - 1, 1))
  const dayCount = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate()
  const days = Array.from({ length: dayCount }, (_, index): ScheduleDay => {
    const date = `${month}-${pad(index + 1)}`
    return {
      date,
      day: index + 1,
      outcomes: records.outcomes.filter((outcome) => outcome.shiftDate === date),
      available: records.available.filter((shift) => shift.date === date),
      bridge: records.bridgeDates.find((preBid) => preBid.date === date) ?? null,
      holiday: records.holidays.find((holiday) => holiday.periodStart.slice(0, 10) <= date && date <= holiday.periodEnd.slice(0, 10)) ?? null,
      scheduled: records.scheduled?.find((shift) => shift.date === date) ?? null,
      drafted: records.drafted?.find((shift) => shift.date === date) ?? null,
      mark: records.marks?.find((entry) => entry.date === date) ?? null,
      isToday: date === today,
    }
  })
  return {
    month,
    label: first.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    leadingBlanks: first.getUTCDay(),
    days,
  }
}

/** The first and last months that have anything to show, with the current month always inside the range. */
export function scheduleRange(records: ScheduleRecords, today: string): { first: string; last: string } {
  const months = [
    today.slice(0, 7),
    ...records.outcomes.map((outcome) => outcome.shiftDate.slice(0, 7)),
    ...records.available.map((shift) => shift.date.slice(0, 7)),
    ...records.bridgeDates.map((preBid) => preBid.date.slice(0, 7)),
    ...(records.scheduled ?? []).map((shift) => shift.date.slice(0, 7)),
    ...(records.drafted ?? []).map((shift) => shift.date.slice(0, 7)),
    ...records.holidays.flatMap((holiday) => [holiday.periodStart.slice(0, 7), holiday.periodEnd.slice(0, 7)]),
  ].sort()
  return { first: months[0] ?? today.slice(0, 7), last: months.at(-1) ?? today.slice(0, 7) }
}

/**
 * The draft's shifts the calendar shows: none once the draft's month has a
 * released schedule (the release is the month then), else every one.
 */
export function draftedShifts(draft: { period: string; assignments: readonly ScheduledShift[] } | null, scheduled: readonly ScheduledShift[]): ScheduledShift[] {
  if (!draft || scheduled.some((shift) => shift.date.startsWith(`${draft.period}-`))) {
    return []
  }
  return draft.assignments.map(({ date, shiftType, startTime, endTime, unitId }) => ({ date, shiftType, startTime, endTime, unitId }))
}
